import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineX } from "react-icons/hi";

import DateTimePicker from "components/DateTimePicker";
import FilePreviewModal, { detectPreviewKind } from "components/FilePreviewModal";
import type { FilePreviewKind } from "components/FilePreviewModal";
import radioCheck from "assets/svg/radio-check.svg";
import uploadDropzoneGraphic from "assets/svg/upload-modal-dropzone-graphic.svg";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";

import {
  generateOppositionCounterStatementPresignedUrls,
  getOppositionCounterByOpponent,
  saveOppositionCounterByOpponent,
  type CounterByOpponentItem,
} from "api/opposition";

export const OPPOSITION_STATUS_DETAILS_FOR_COUNTER_FORM_ID =
  "opposition-status-details-for-counter-form";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const MAX_FILE_SIZE = 50 * 1024 * 1024;

function RadioCircle({ active }: { active: boolean }) {
  if (active) {
    return <img src={radioCheck} alt="" width={14} height={14} />;
  }
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

function parseDateStr(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function toISODate(d: Date | null): string | null {
  if (!d) return null;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

function addMonths(d: Date, months: number): Date {
  const result = new Date(d);
  result.setMonth(result.getMonth() + months);
  return result;
}

type RowStatus = "empty" | "in-progress" | "complete" | "warning" | "overdue";

function deriveRowStatus(row: RowState, today: Date): RowStatus {
  if (row.counterFiled == null) return "empty";
  if (row.counterFiled === false) return "complete";

  const hasAllDates =
    !!row.dateOfCounter &&
    !!row.dateOfReceiptOfCounter &&
    !!row.lastDateOfFilingEvidence;
  const hasFile = !!row.counterStatementFileKey || !!row.pendingFile;
  if (!hasAllDates || !hasFile) return "in-progress";

  if (row.lastDateOfFilingEvidence) {
    const msPerDay = 1000 * 60 * 60 * 24;
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const target = new Date(
      row.lastDateOfFilingEvidence.getFullYear(),
      row.lastDateOfFilingEvidence.getMonth(),
      row.lastDateOfFilingEvidence.getDate()
    );
    const daysUntil = Math.round((target.getTime() - start.getTime()) / msPerDay);
    if (daysUntil < 0) return "overdue";
    if (daysUntil <= 15) return "warning";
  }
  return "complete";
}

function StatusDot({ status }: { status: RowStatus }) {
  if (status === "empty") return null;
  const config: Record<Exclude<RowStatus, "empty">, { color: string; title: string }> = {
    "in-progress": { color: "#F59E0B", title: "In progress" },
    complete: { color: "#16A34A", title: "Complete" },
    warning: { color: "#EA580C", title: "Evidence deadline within 15 days" },
    overdue: { color: "#DC2626", title: "Evidence deadline passed" },
  };
  const { color, title } = config[status];
  return (
    <span
      aria-label={title}
      title={title}
      className="inline-block w-[7px] h-[7px] rounded-full flex-shrink-0"
      style={{ backgroundColor: color }}
    />
  );
}

type RowState = {
  conflictingMarkId: string;
  counterFiled: boolean | null;
  dateOfCounter: Date | null;
  dateOfReceiptOfCounter: Date | null;
  lastDateOfFilingEvidence: Date | null;
  lastDateOverridden: boolean;
  counterStatementFileKey: string | null;
  counterStatementFilename: string | null;
  counterStatementDownloadUrl: string | null;
  pendingFile: File | null;
};

function makeRowFromItem(item: CounterByOpponentItem): RowState {
  return {
    conflictingMarkId: item.id,
    counterFiled: item.counterFiled,
    dateOfCounter: parseDateStr(item.dateOfCounter),
    dateOfReceiptOfCounter: parseDateStr(item.dateOfReceiptOfCounter),
    lastDateOfFilingEvidence: parseDateStr(item.lastDateOfFilingEvidence),
    lastDateOverridden: false,
    counterStatementFileKey: item.counterStatementFileKey,
    counterStatementFilename: item.counterStatementFilename,
    counterStatementDownloadUrl: item.counterStatementDownloadUrl,
    pendingFile: null,
  };
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionStatusDetailsForCounterView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [marks, setMarks] = useState<CounterByOpponentItem[]>([]);
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [selectedIdx, setSelectedIdx] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [preview, setPreview] = useState<{
    url: string;
    name: string;
    kind: FilePreviewKind;
    revokeOnClose?: boolean;
  } | null>(null);

  useEffect(() => {
    if (!leadId) {
      setMarks([]);
      setRows({});
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getOppositionCounterByOpponent(leadId)
      .then((data) => {
        if (cancelled) return;
        setMarks(data.conflictingMarks);
        const r: Record<string, RowState> = {};
        for (const m of data.conflictingMarks) {
          r[m.id] = makeRowFromItem(m);
        }
        setRows(r);
        // Jump to the first application that still needs work so staff
        // land on actionable input instead of always the first item.
        const today = new Date();
        const firstIncomplete = data.conflictingMarks.findIndex((m) => {
          const status = deriveRowStatus(r[m.id], today);
          return status !== "complete";
        });
        setSelectedIdx(firstIncomplete >= 0 ? firstIncomplete : 0);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load Counter details"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const selectedMark = marks[selectedIdx];
  const selectedRow = selectedMark ? rows[selectedMark.id] : undefined;

  const updateRow = (id: string, patch: Partial<RowState>) => {
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const handleReceiptDateChange = (id: string, d: Date | null) => {
    setRows((prev) => {
      const current = prev[id];
      if (!current) return prev;
      const next: RowState = {
        ...current,
        dateOfReceiptOfCounter: d,
      };
      if (!next.lastDateOverridden) {
        next.lastDateOfFilingEvidence = d ? addMonths(d, 2) : null;
      }
      return { ...prev, [id]: next };
    });
  };

  const handleLastDateOverride = (id: string, d: Date | null) => {
    setRows((prev) => {
      const current = prev[id];
      if (!current) return prev;
      return {
        ...prev,
        [id]: { ...current, lastDateOfFilingEvidence: d, lastDateOverridden: true },
      };
    });
  };

  const onFileChosen = (id: string, file: File | null) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`${file.name} exceeds the 50MB limit`);
      return;
    }
    updateRow(id, { pendingFile: file });
  };

  const removeUploadedFile = (id: string) => {
    updateRow(id, {
      counterStatementFileKey: null,
      counterStatementFilename: null,
      counterStatementDownloadUrl: null,
      pendingFile: null,
    });
  };

  const openSavedPreview = (row: RowState) => {
    if (!row.counterStatementDownloadUrl || !row.counterStatementFilename) {
      toast.error("Preview is not available for this file");
      return;
    }
    setPreview({
      url: row.counterStatementDownloadUrl,
      name: row.counterStatementFilename,
      kind: detectPreviewKind(row.counterStatementFilename),
    });
  };

  const openPendingPreview = (file: File) => {
    const url = URL.createObjectURL(file);
    setPreview({
      url,
      name: file.name,
      kind: detectPreviewKind(file.name, file.type),
      revokeOnClose: true,
    });
  };

  const closePreview = () => {
    if (preview?.revokeOnClose) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const validateForSubmit = (): string | null => {
    const ids = Object.keys(rows);
    if (ids.length === 0) return "No eligible conflicting applications found.";
    for (const id of ids) {
      const r = rows[id];
      if (r.counterFiled == null) {
        return "Please select YES or NO for every application.";
      }
      if (r.counterFiled === true) {
        if (!r.dateOfCounter) return "Date of Counter is required when Counter is filed.";
        if (!r.dateOfReceiptOfCounter)
          return "Date of Receipt of Counter is required when Counter is filed.";
        if (!r.lastDateOfFilingEvidence)
          return "Last date of filing Evidence is required when Counter is filed.";
        if (!r.counterStatementFileKey && !r.pendingFile)
          return "Please upload the Counter Statement PDF for every application where Counter is filed.";
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    const validationError = validateForSubmit();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSaving(true);
    try {
      // Upload any pending Counter Statement PDFs first.
      const uploadedKeys = new Map<string, { fileKey: string; fileName: string }>();
      const pendingUploads = Object.values(rows).filter((r) => r.pendingFile);
      if (pendingUploads.length > 0) {
        const presigned = await generateOppositionCounterStatementPresignedUrls(
          leadId,
          pendingUploads.map((r) => ({
            filename: r.pendingFile!.name,
            mimeType: r.pendingFile!.type || "application/pdf",
          }))
        );
        if (presigned.length !== pendingUploads.length) {
          throw new Error("Failed to generate upload URLs");
        }
        await Promise.all(
          pendingUploads.map(async (r, i) => {
            const target = presigned[i];
            const putRes = await fetch(target.url, {
              method: "PUT",
              body: r.pendingFile!,
              headers: { "Content-Type": r.pendingFile!.type || "application/pdf" },
            });
            if (!putRes.ok) throw new Error(`Failed to upload ${r.pendingFile!.name}`);
            uploadedKeys.set(r.conflictingMarkId, {
              fileKey: presigned[i].key,
              fileName: r.pendingFile!.name,
            });
          })
        );
      }

      const payload = {
        conflictingMarks: Object.values(rows).map((r) => {
          const uploaded = uploadedKeys.get(r.conflictingMarkId);
          const fileKey = uploaded?.fileKey ?? r.counterStatementFileKey;
          const fileName = uploaded?.fileName ?? r.counterStatementFilename;
          return {
            conflictingMarkId: r.conflictingMarkId,
            counterFiled: r.counterFiled,
            dateOfCounter: r.counterFiled ? toISODate(r.dateOfCounter) : null,
            dateOfReceiptOfCounter: r.counterFiled
              ? toISODate(r.dateOfReceiptOfCounter)
              : null,
            lastDateOfFilingEvidence: r.counterFiled
              ? toISODate(r.lastDateOfFilingEvidence)
              : null,
            counterStatementFileKey: r.counterFiled ? fileKey : null,
            counterStatementFilename: r.counterFiled ? fileName : null,
          };
        }),
      };

      const fresh = await saveOppositionCounterByOpponent(leadId, payload);
      setMarks(fresh.conflictingMarks);
      const refreshed: Record<string, RowState> = {};
      for (const m of fresh.conflictingMarks) refreshed[m.id] = makeRowFromItem(m);
      setRows(refreshed);
      toast.success("Status details for Counter saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  if (marks.length === 0) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          No conflicting applications are eligible yet. Please complete Stage 2 Step 4
          (Notice of Opposition by Registry) first.
        </p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_STATUS_DETAILS_FOR_COUNTER_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* TM (Application No.) selector */}
      {marks.length > 1 && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-center justify-between gap-3">
            <div className="flex flex-row flex-wrap gap-3 flex-1 min-w-0">
              {marks.map((m, idx) => {
                const isActive = selectedIdx === idx;
                const status = deriveRowStatus(rows[m.id], new Date());
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setSelectedIdx(idx)}
                    className={`flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] transition-colors ${
                      isActive
                        ? "bg-[#222222] text-white"
                        : "bg-[#F7F7F7] text-[#222222]"
                    }`}
                    style={{ fontSize: "0.75rem" }}
                  >
                    <RadioCircle active={isActive} />
                    <span className="flex-1 text-left">
                      {formatTmNoForDisplay(m.applicationNo ?? "")}
                    </span>
                    <StatusDot status={status} />
                  </button>
                );
              })}
            </div>
            <span className="relative inline-flex items-center group flex-shrink-0">
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                className="cursor-help"
              >
                <circle cx="8" cy="8" r="6.5" stroke="#666666" strokeWidth="1.25" />
                <path d="M8 7.5 V11" stroke="#666666" strokeWidth="1.25" strokeLinecap="round" />
                <circle cx="8" cy="5.33" r="0.6" fill="#666666" />
              </svg>
              <span
                role="tooltip"
                className="hidden group-hover:flex flex-col gap-1 absolute z-20 right-0 top-[20px] rounded-[6px] text-white pointer-events-none"
                style={{
                  width: "240px",
                  maxWidth: "calc(100vw - 40px)",
                  padding: "8px 10px",
                  background: "#565656",
                  fontSize: "11px",
                  lineHeight: "16px",
                  fontWeight: 400,
                }}
              >
                <span className="font-medium">Status indicators</span>
                <span className="flex items-center gap-2">
                  <StatusDot status="in-progress" />
                  In progress
                </span>
                <span className="flex items-center gap-2">
                  <StatusDot status="complete" />
                  Complete
                </span>
                <span className="flex items-center gap-2">
                  <StatusDot status="warning" />
                  Evidence deadline ≤ 15 days
                </span>
                <span className="flex items-center gap-2">
                  <StatusDot status="overdue" />
                  Evidence deadline passed
                </span>
              </span>
            </span>
          </div>
        </div>
      )}

      {selectedMark && selectedRow && (
        <>
          {/* Date of Receipt of Opposition + Last date of filing Counter */}
          <div className="grid grid-cols-2 gap-5">
            <div className="flex flex-col gap-1.5">
              <label className={LABEL_CLASS}>
                Date of Receipt of Opposition <span className="text-red-500">*</span>
              </label>
              <DateTimePicker
                value={parseDateStr(selectedMark.dateOfNoticeOfOpposition)}
                placeholder="—"
                readOnly
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-1">
                <label className={LABEL_CLASS}>Last date of filing Counter</label>
                <span className="relative inline-flex items-center group">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="cursor-help flex-shrink-0">
                    <circle cx="8" cy="8" r="6.5" stroke="#666666" strokeWidth="1.25" />
                    <path d="M8 7.5 V11" stroke="#666666" strokeWidth="1.25" strokeLinecap="round" />
                    <circle cx="8" cy="5.33" r="0.6" fill="#666666" />
                  </svg>
                  <span role="tooltip" className="hidden group-hover:flex absolute z-20 left-0 top-[20px] rounded-[6px] text-white pointer-events-none" style={{ width: "280px", maxWidth: "calc(100vw - 40px)", padding: "6px 10px", background: "#565656", fontSize: "11px", lineHeight: "18px", fontWeight: 400 }}>
                    Auto-calculated as Date of Receipt of Opposition + 2 months.
                  </span>
                </span>
              </div>
              <DateTimePicker
                value={parseDateStr(selectedMark.lastDateForCounter)}
                placeholder="—"
                readOnly
              />
            </div>
          </div>

          {/* YES / NO toggle */}
          <div className="flex flex-row flex-wrap gap-3">
            {[
              { id: true, label: "YES" },
              { id: false, label: "NO" },
            ].map((opt) => {
              const isActive = selectedRow.counterFiled === opt.id;
              return (
                <button
                  key={String(opt.id)}
                  type="button"
                  onClick={() =>
                    updateRow(selectedMark.id, { counterFiled: opt.id })
                  }
                  className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[160px] text-xs font-normal transition-colors ${
                    isActive
                      ? "bg-[#222222] text-white"
                      : "bg-[#F7F7F7] text-[#222222]"
                  }`}
                >
                  <RadioCircle active={isActive} />
                  <span className="text-left">{opt.label}</span>
                </button>
              );
            })}
          </div>

          {selectedRow.counterFiled === true && (
            <>
              <div className="grid grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5">
                  <label className={LABEL_CLASS}>
                    Date of Counter from Conflicting Proprietor
                  </label>
                  <DateTimePicker
                    value={selectedRow.dateOfCounter}
                    onChange={(d) =>
                      updateRow(selectedMark.id, { dateOfCounter: d })
                    }
                    placeholder="Text Here"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className={LABEL_CLASS}>
                    Date of Receipt of Counter from Conflicting Proprietor
                  </label>
                  <DateTimePicker
                    value={selectedRow.dateOfReceiptOfCounter}
                    onChange={(d) => handleReceiptDateChange(selectedMark.id, d)}
                    placeholder="Text Here"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-1">
                    <label className={LABEL_CLASS}>Last date of filing Evidence</label>
                    <span className="relative inline-flex items-center group">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="cursor-help flex-shrink-0">
                        <circle cx="8" cy="8" r="6.5" stroke="#666666" strokeWidth="1.25" />
                        <path d="M8 7.5 V11" stroke="#666666" strokeWidth="1.25" strokeLinecap="round" />
                        <circle cx="8" cy="5.33" r="0.6" fill="#666666" />
                      </svg>
                      <span role="tooltip" className="hidden group-hover:flex absolute z-20 left-0 top-[20px] rounded-[6px] text-white pointer-events-none" style={{ width: "280px", maxWidth: "calc(100vw - 40px)", padding: "6px 10px", background: "#565656", fontSize: "11px", lineHeight: "18px", fontWeight: 400 }}>
                        Auto-calculated as Date of Receipt of Counter from Conflicting Proprietor + 2 months. You can override it manually.
                      </span>
                    </span>
                  </div>
                  <DateTimePicker
                    value={selectedRow.lastDateOfFilingEvidence}
                    onChange={(d) => handleLastDateOverride(selectedMark.id, d)}
                    placeholder="Auto-calculated"
                  />
                </div>
              </div>

              {/* Counter Statement PDF upload (API requires this) */}
              <div className="flex flex-col gap-2.5">
                <label className={LABEL_CLASS}>Counter Statement PDF</label>
                {selectedRow.counterStatementFileKey &&
                selectedRow.counterStatementFilename ? (
                  <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]">
                    <span className="text-xs text-[#222222] truncate min-w-0 flex-1">
                      {selectedRow.counterStatementFilename}
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => openSavedPreview(selectedRow)}
                        className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                      >
                        Preview
                      </button>
                      {selectedRow.counterStatementDownloadUrl && (
                        <a
                          href={selectedRow.counterStatementDownloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={selectedRow.counterStatementFilename}
                          className="px-3 h-8 inline-flex items-center justify-center rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                        >
                          Download
                        </a>
                      )}
                      <button
                        type="button"
                        aria-label="Remove file"
                        onClick={() => removeUploadedFile(selectedMark.id)}
                        disabled={saving}
                        className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                      >
                        <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                ) : selectedRow.pendingFile ? (
                  <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-white">
                    <span className="text-xs text-[#16A34A] truncate min-w-0 flex-1">
                      {selectedRow.pendingFile.name}
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => openPendingPreview(selectedRow.pendingFile!)}
                        className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        aria-label="Remove file"
                        onClick={() => updateRow(selectedMark.id, { pendingFile: null })}
                        disabled={saving}
                        className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                      >
                        <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex flex-row justify-between items-center gap-5 px-5 py-4 rounded-[16px] border border-dashed border-[#D8DAEB] bg-white cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="flex flex-row items-center gap-4 min-w-0">
                      <img src={uploadDropzoneGraphic} alt="" width={48} height={48} />
                      <div className="flex flex-col gap-1 min-w-0">
                        <p
                          className="font-medium text-[#101111]"
                          style={{ fontSize: "14px", lineHeight: "100%" }}
                        >
                          Upload Counter Statement
                        </p>
                        <p
                          className="font-normal text-[#7D7D7D]"
                          style={{ fontSize: "12px", lineHeight: "18px" }}
                        >
                          PDF up to 50MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[10px] bg-white border border-[#D5D3E0] text-[#2E3037] hover:bg-gray-50"
                      style={{ fontSize: "13px", fontWeight: 500 }}
                    >
                      Select File
                    </button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    onFileChosen(selectedMark.id, f);
                    e.currentTarget.value = "";
                  }}
                />
              </div>
            </>
          )}

          {/* Image of Conflicting Mark — always shown last */}
          {selectedMark.imageOfMarkUrl && (
            <div className="flex flex-col gap-1.5">
              <label className={LABEL_CLASS}>Image of Conflicting Mark</label>
              <div className="w-full rounded-[8px] border border-[#EEE] bg-[#F9F9F9] p-3 flex justify-center">
                <img
                  src={selectedMark.imageOfMarkUrl}
                  alt="Conflicting Mark"
                  className="max-h-[120px] object-contain"
                />
              </div>
            </div>
          )}
        </>
      )}

      <FilePreviewModal
        open={!!preview}
        onClose={closePreview}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />
    </form>
  );
}
